// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Administrative audit facts.
 *
 * The BFF supplies a minimised projection: actor, action, outcome, resource,
 * and correlation identifier. `SEC2-050` and `DAT-006` keep the originating
 * identity context out of an administrative view that does not need it, and
 * `PHY-010` keeps audit itself immutable — this console reads, never edits.
 */

import { capabilities, queryKeys, StateBadge, type AuditEventView } from '@eai/ui-shared';
import type { ReactNode } from 'react';

import { ResourceScreen, useControlPlaneQuery } from '../app-shell';

const OUTCOME_TONE = {
  success: 'positive',
  failure: 'negative',
  denied: 'caution',
  unknown: 'neutral',
} as const;

/** Administrative audit screen. */
export function AuditScreen(): ReactNode {
  const query = useControlPlaneQuery(
    (scope) => queryKeys.controlPlane.auditEvents(scope),
    (client, signal) => client.listAuditEvents({ signal }),
  );

  return (
    <ResourceScreen<AuditEventView>
      title="Audit"
      description="Attributable administrative facts. Audit records are immutable; this console reads them and cannot alter them."
      requiredCapability={capabilities.auditRead}
      area="Audit"
      query={query}
      rowKey={(row) => row.event_id}
      emptyTitle="No audit fact matched"
      emptyDescription="No administrative activity was recorded for this scope and period."
      columns={[
        { key: 'timestamp', header: 'When', render: (row) => row.timestamp },
        {
          key: 'actor',
          header: 'Actor',
          render: (row) =>
            `${row.actor.display_name ?? row.actor.actor_id} (${row.actor.actor_type})`,
        },
        { key: 'action', header: 'Action', render: (row) => row.action },
        {
          key: 'resource',
          header: 'Resource',
          render: (row) => row.resource_label ?? row.resource_type ?? 'Not reported',
        },
        {
          key: 'outcome',
          header: 'Outcome',
          render: (row) => (
            <StateBadge
              label={row.outcome}
              tone={OUTCOME_TONE[row.outcome]}
              subject={`${row.action} outcome`}
            />
          ),
        },
        {
          key: 'correlation',
          header: 'Correlation',
          render: (row) => row.correlation_id ?? 'Not reported',
        },
      ]}
    />
  );
}
